// ReplayManagement.tsx 내부 예시
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { userState } from '../../../interface/MainInterface';
import ReplaySelectionDialog from './ReplaySelectionDialog';
import { HttpAdminApi } from '../../../interface/admin-api';
import dayjs from 'dayjs';
import Toast from '../../../utils/Toast';

interface PropsType {
    userState: userState;
    channelInfo: any;
    callback: any;
}

const adminApi = new HttpAdminApi();

export const ReplayManagement = ({ userState, channelInfo, callback }: PropsType) => {
  const toastRef: any = useRef();
  const [open, setOpen] = useState(false);

  // 현재 시간 기준 1시간 전인지 계산 (모든 곳에서 공통 사용)
  const getIsDisable = () => {
    if (!channelInfo?.broad_start_tm) return false;
    const startTime = dayjs(channelInfo.broad_start_tm, "YYYYMMDDHHmmss");
    return dayjs().add(1, 'hour').isAfter(startTime);
  };

  const isDisable = getIsDisable();
  // 재방송 설정 가능 상태 확인 (CREATE 또는 READY)
  const isAvailableStatus = 
    (channelInfo?.broad_status === "CREATE" || channelInfo?.broad_status === "READY") && !isDisable;
  
  // 비활성화 사유를 결정하는 로직
  const getTooltipTitle = () => {
    if (isDisable) return "방송 시작 1시간 전부터는 재방송 설정을 변경할 수 없습니다.";
    if (channelInfo?.broad_status !== "CREATE" && channelInfo?.broad_status !== "READY") return "방송 시작 전 상태에서만 설정 가능합니다.";
    return "재방송 영상 선택";
  };

  const handleVideoSelect = async (selectedReplayChannel: any) => {
    if (getIsDisable()) {
      // 시간 제한에 걸리면 알림을 띄우고 강제 갱신(새로고침 효과)
      toastRef.current?.toast("방송 시작 1시간 전부터는 재방송 설정을 변경할 수 없습니다.", "error", 3000, {
        vertical: "top", horizontal: "center",
      });
      callback({ command: "refresh_channel" }); // 부모에게 새로고침 요청
      setOpen(false);
      return;
    }

    if (!isAvailableStatus) return;

    try {
      // 1. 재방송 설정 저장 API 호출 (별도 데이터 행으로 저장)
      const param = {
        command: "update_replay_info", // 별도 설정 테이블 업데이트 명령
        search_condition: {
          channel_info: channelInfo,      // 현재 관리중인 라이브 채널 번호
          replay_channel_info: selectedReplayChannel,    // 재방송 정보
          use_yn: "Y",
        },
        // request_user_id: userState.id,
      };

      // adminApi.post 호출 (프로젝트 환경에 맞는 API 모듈 사용)
      const res = await adminApi.post(param);

      if (res.code === "200") {
        // 2. 부모 컴포넌트에 성공 알림 및 데이터 전달
        callback({
          command: "set_replay_video",
          value: selectedReplayChannel, // UI 표시용 원본 데이터
        });
      } else {
        callback({
          command: "toast",
          msg: "재방송 설정 저장에 실패했습니다.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setOpen(false);
    }
  };

  const handleCancelReplay = async () => {
    try {
      const param = {
        command: "update_replay_info",
        search_condition: {
          channel_info: channelInfo,
          use_yn: "N"
        },
        // request_user_id: userState.id,
      };

      const res = await adminApi.post(param);
      if (res.code === "200") {
        // 부모 컴포넌트에 알림 (선택된 영상 정보를 null로 날림)
        callback({ command: "set_replay_video", value: null }); 
        setOpen(false);
      }
    } catch (error) {
      console.error("재방송 취소 실패:", error);
    }
  };
  
  return (
    <>
      <Tooltip title={getTooltipTitle()}>
        <Box
          sx={{
            cursor: isAvailableStatus ? "pointer" : "not-allowed",
            minWidth: "70px",
            opacity: isAvailableStatus ? 1 : 0.5, // 상태가 아닐 경우 반투명 처리
          }}
          onClick={() => {
            // 1. 클릭 시점에 실시간으로 1시간 전인지 다시 체크
            const nowIsDisable = getIsDisable(); 

            // 2. 비활성화 조건에 해당하면 Dialog를 열지 않고 즉시 리턴
            if (nowIsDisable) {
              toastRef.current?.toast("방송 시작 1시간 전부터는 재방송 설정을 변경할 수 없습니다.", "error", 3000, {
                vertical: "top", horizontal: "center",
              });
              callback({ command: "refresh_channel" }); // 부모에게 새로고침 요청
              return;
            }

            // 3. 기존의 상태 체크 (CREATE/READY 등)
            if (isAvailableStatus) setOpen(true);
          }}
        >
          <Stack
            direction={"column"}
            spacing={0}
            sx={{
              backgroundColor: "#4caf50",
              color: "#ffffff",
              borderRadius: "7px",
              p: 1,
              transition: "0.2s",
              "&:hover": { backgroundColor: isAvailableStatus ? "#388e3c" : "#4caf50" },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <PlayCircleOutlineIcon />
            </Box>
            <Typography sx={{ fontSize: "14px", textAlign: "center" }}>재방송설정</Typography>
          </Stack>
        </Box>
      </Tooltip>

      <ReplaySelectionDialog
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleVideoSelect} // 여기서 API 호출 후 최종 처리
        onCancel={handleCancelReplay}
        userState={userState}
      />
      
      <Toast ref={toastRef} />
    </>
  );
};